import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import {
  NivelAfectacion,
  IInfraestructuraItem,
} from '@/shared/interfaces/erp/tickets-soporte-clientes/gestion-ticket.interface';
import {
  useFetchInfraestructuraMasivo,
  useFetchInfraestructuraMasivoMulti,
  useFetchPreviewClientesMasivo,
} from '@/shared/actions/erp/tickets-soporte-cliente/gestion-tickets-masivos.actions';
import {
  useFetchTicketTipos,
  useFetchTicketCategorias,
  useFetchTicketSubcategorias,
} from '@/shared/actions/erp/tickets-soporte-cliente';
import FileDropzoneField from '@/shared/components/ui/FileDropzoneField';

export type FormMasivoValues = {
  descripcion: string;
  nivel_afectacion: NivelAfectacion;
  olts_afectadas: string[];
  troncales_afectadas: string[];
  naps_afectadas: string[];
  routers_afectados: string[];
  // Clasificacion (opcional). El pool del select se filtra por
  // aplica_masivos=true del catalogo + se excluye categoria
  // 'motivos_cierre' (esas son las de cierre al resolver).
  tipo_id: string;
  categoria_id: string;
  subcategoria_id: string;
  // Flag para indicar si el reporte tiene afectacion real (default true)
  // o es falsa alarma (false). Se persiste como tiene_afectacion.
  tiene_afectacion: boolean;
};

const NIVEL_OPTIONS: { value: NivelAfectacion; label: string }[] = [
  { value: 'olt', label: 'OLT' },
  { value: 'troncal', label: 'Troncal (Caja Primaria)' },
  { value: 'nap', label: 'NAP' },
  { value: 'router', label: 'Router' },
];

const schema = yup.object().shape({
  descripcion: yup.string().required('La descripcion es requerida'),
  nivel_afectacion: yup
    .string()
    .oneOf(['olt', 'troncal', 'nap', 'router'])
    .required('Seleccione un nivel de afectacion'),
  olts_afectadas: yup
    .array()
    .of(yup.string().required())
    .when('nivel_afectacion', {
      is: (val: string) => ['olt', 'troncal', 'nap'].includes(val),
      then: (s) => s.min(1, 'Seleccione al menos una OLT').required(),
      otherwise: (s) => s.default([]),
    }),
  troncales_afectadas: yup
    .array()
    .of(yup.string().required())
    .when('nivel_afectacion', {
      is: (val: string) => ['troncal', 'nap'].includes(val),
      then: (s) => s.min(1, 'Seleccione al menos una troncal').required(),
      otherwise: (s) => s.default([]),
    }),
  naps_afectadas: yup
    .array()
    .of(yup.string().required())
    .when('nivel_afectacion', {
      is: 'nap',
      then: (s) => s.min(1, 'Seleccione al menos una NAP').required(),
      otherwise: (s) => s.default([]),
    }),
  routers_afectados: yup
    .array()
    .of(yup.string().required())
    .when('nivel_afectacion', {
      is: 'router',
      then: (s) => s.min(1, 'Seleccione al menos un router').required(),
      otherwise: (s) => s.default([]),
    }),
});

export type FormMasivoProps = {
  // onSubmit recibe los valores del form + la lista (posiblemente vacia)
  // de archivos cargados via drag&drop. El caller decide si usar el
  // endpoint JSON clasico o el multipart segun archivos.length > 0.
  onSubmit: (data: FormMasivoValues, archivos: File[]) => void;
  isLoading?: boolean;
};

const FormMasivo: React.FC<FormMasivoProps> = ({
  onSubmit,
  isLoading = false,
}) => {
  const [archivos, setArchivos] = useState<File[]>([]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormMasivoValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      descripcion: '',
      nivel_afectacion: 'nap',
      olts_afectadas: [],
      troncales_afectadas: [],
      naps_afectadas: [],
      routers_afectados: [],
      tipo_id: '',
      categoria_id: '',
      subcategoria_id: '',
      tiene_afectacion: true,
    },
  });

  const nivel = watch('nivel_afectacion');
  const oltsSeleccionadas = watch('olts_afectadas') || [];
  const troncalesSeleccionadas = watch('troncales_afectadas') || [];
  const napsSeleccionadas = watch('naps_afectadas') || [];
  const routersSeleccionados = watch('routers_afectados') || [];

  // Fetch OLTs cuando el nivel requiere el paso OLT
  const showOlt = ['olt', 'troncal', 'nap'].includes(nivel);
  const { data: oltsData, isLoading: isLoadingOlts } =
    useFetchInfraestructuraMasivo(showOlt ? 'olt' : '');
  const olts: IInfraestructuraItem[] =
    (oltsData?.data as any)?.data ?? (oltsData?.data as any) ?? [];

  // Fetch troncales: para multi-padre usamos el hook multi (una sola
  // llamada a useQuery, respeta rules of hooks). El hook se encarga de
  // paralelizar y dedupe por codigo.
  const showTroncal = ['troncal', 'nap'].includes(nivel);
  const { data: troncalesMultiData, isLoading: isLoadingTroncales } =
    useFetchInfraestructuraMasivoMulti(
      showTroncal ? 'troncal' : '',
      oltsSeleccionadas,
    );
  const troncales: IInfraestructuraItem[] =
    (troncalesMultiData?.data as any)?.data ?? [];

  // Fetch NAPs: mismo patron multi-padre (una por troncal seleccionada)
  const showNap = nivel === 'nap';
  const { data: napsMultiData, isLoading: isLoadingNaps } =
    useFetchInfraestructuraMasivoMulti(
      showNap ? 'nap' : '',
      troncalesSeleccionadas,
    );
  const naps: IInfraestructuraItem[] =
    (napsMultiData?.data as any)?.data ?? [];

  // Fetch routers
  const showRouter = nivel === 'router';
  const { data: routersData, isLoading: isLoadingRouters } =
    useFetchInfraestructuraMasivo(showRouter ? 'router' : '');
  const routers: IInfraestructuraItem[] =
    (routersData?.data as any)?.data ?? (routersData?.data as any) ?? [];

  // Clasificacion opcional: Tipo -> Categoria -> Subcategoria, en cascada.
  // Todos filtran por aplica_masivos=true. Las subcategorias bajo la
  // categoria 'motivos_cierre' se usan al resolver el masivo, no aqui.
  const tipoSeleccionado = watch('tipo_id');
  const categoriaSeleccionada = watch('categoria_id');

  const { data: tiposData, isLoading: isLoadingTipos } = useFetchTicketTipos(true);
  const tipos: any[] =
    (tiposData?.data as any)?.data ?? (tiposData?.data as any) ?? [];

  const { data: categoriasData, isLoading: isLoadingCategorias } =
    useFetchTicketCategorias(tipoSeleccionado || undefined, true);
  const categoriasAll: any[] =
    (categoriasData?.data as any)?.data ?? (categoriasData?.data as any) ?? [];
  // Excluimos 'motivos_cierre' del pool de apertura. Se usa en resolver.
  const categorias = categoriasAll.filter((c) => c.codigo !== 'motivos_cierre');

  const { data: subcategoriasData, isLoading: isLoadingSubcategorias } =
    useFetchTicketSubcategorias(categoriaSeleccionada || undefined, true);
  const subcategorias: any[] =
    (subcategoriasData?.data as any)?.data ?? (subcategoriasData?.data as any) ?? [];

  // Preview clientes afectados: el backend acepta CSV por query param.
  const previewParams = {
    olt: nivel === 'olt' && oltsSeleccionadas.length > 0
      ? oltsSeleccionadas.join(',') : undefined,
    troncal: nivel === 'troncal' && troncalesSeleccionadas.length > 0
      ? troncalesSeleccionadas.join(',') : undefined,
    nap: nivel === 'nap' && napsSeleccionadas.length > 0
      ? napsSeleccionadas.join(',') : undefined,
    router: nivel === 'router' && routersSeleccionados.length > 0
      ? routersSeleccionados.join(',') : undefined,
  };
  const { data: previewData, isLoading: isLoadingPreview } =
    useFetchPreviewClientesMasivo(nivel as NivelAfectacion, previewParams);
  const previewCount: number | null =
    (previewData as any)?.data?.data?.total_afectados
    ?? (previewData as any)?.data?.data?.clientes_afectados_count
    ?? null;

  // Reset en cascada al cambiar nivel / padres
  useEffect(() => {
    setValue('olts_afectadas', []);
    setValue('troncales_afectadas', []);
    setValue('naps_afectadas', []);
    setValue('routers_afectados', []);
  }, [nivel, setValue]);

  useEffect(() => {
    // Al cambiar el set de OLTs, reseteamos troncales+naps. El usuario
    // vuelve a seleccionar dentro de la union resultante.
    setValue('troncales_afectadas', []);
    setValue('naps_afectadas', []);
  }, [oltsSeleccionadas.join(','), setValue]);

  useEffect(() => {
    setValue('naps_afectadas', []);
  }, [troncalesSeleccionadas.join(','), setValue]);

  // Reset cascada de clasificacion: al cambiar tipo se resetea categoria
  // y subcategoria; al cambiar categoria se resetea subcategoria.
  useEffect(() => {
    setValue('categoria_id', '');
    setValue('subcategoria_id', '');
  }, [tipoSeleccionado, setValue]);
  useEffect(() => {
    setValue('subcategoria_id', '');
  }, [categoriaSeleccionada, setValue]);

  return (
    <Box component="form" onSubmit={handleSubmit((data) => onSubmit(data, archivos))}>
      <Grid container spacing={3}>
        {/* Nivel de afectacion */}
        <Grid item xs={12}>
          <Controller
            name="nivel_afectacion"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Nivel de Afectacion"
                fullWidth
                error={!!errors.nivel_afectacion}
                helperText={errors.nivel_afectacion?.message}
              >
                {NIVEL_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Paso 1: OLTs */}
        {showOlt && (
          <Grid item xs={12} md={nivel === 'olt' ? 12 : 6}>
            <Controller
              name="olts_afectadas"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={olts}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string'
                      ? opt
                      : `${opt.nombre || ''} - ${opt.codigo}`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    (typeof opt === 'string' ? opt : opt.codigo) ===
                    (typeof val === 'string' ? val : val.codigo)
                  }
                  value={olts.filter((o) => field.value?.includes(o.codigo))}
                  onChange={(_, newVal) =>
                    field.onChange(
                      newVal.map((v) => (typeof v === 'string' ? v : v.codigo)),
                    )
                  }
                  loading={isLoadingOlts}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={typeof opt === 'string' ? opt : opt.codigo}
                        label={typeof opt === 'string' ? opt : opt.nombre || opt.codigo}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={nivel === 'olt' ? 'OLTs afectadas' : 'Paso 1: OLT(s) (padre)'}
                      placeholder="Buscar OLT..."
                      error={!!errors.olts_afectadas}
                      helperText={
                        (errors.olts_afectadas as any)?.message
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingOlts && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}

        {/* Paso 2: Troncales */}
        {showTroncal && oltsSeleccionadas.length > 0 && (
          <Grid item xs={12} md={6}>
            <Controller
              name="troncales_afectadas"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={troncales}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string'
                      ? opt
                      : `${opt.nombre || ''} - ${opt.codigo}`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    (typeof opt === 'string' ? opt : opt.codigo) ===
                    (typeof val === 'string' ? val : val.codigo)
                  }
                  value={troncales.filter((t) =>
                    field.value?.includes(t.codigo),
                  )}
                  onChange={(_, newVal) =>
                    field.onChange(
                      newVal.map((v) => (typeof v === 'string' ? v : v.codigo)),
                    )
                  }
                  loading={isLoadingTroncales}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={typeof opt === 'string' ? opt : opt.codigo}
                        label={typeof opt === 'string' ? opt : opt.nombre || opt.codigo}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        nivel === 'troncal'
                          ? 'Paso 2: Troncales afectadas'
                          : 'Paso 2: Troncal(es) (padre)'
                      }
                      placeholder="Buscar troncal..."
                      error={!!errors.troncales_afectadas}
                      helperText={
                        (errors.troncales_afectadas as any)?.message
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingTroncales && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}

        {/* Paso 3: NAPs */}
        {showNap && troncalesSeleccionadas.length > 0 && (
          <Grid item xs={12}>
            <Controller
              name="naps_afectadas"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={naps}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string'
                      ? opt
                      : `${opt.nombre || ''} - ${opt.codigo}`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    (typeof opt === 'string' ? opt : opt.codigo) ===
                    (typeof val === 'string' ? val : val.codigo)
                  }
                  value={naps.filter((n) => field.value?.includes(n.codigo))}
                  onChange={(_, newVal) =>
                    field.onChange(
                      newVal.map((v) => (typeof v === 'string' ? v : v.codigo)),
                    )
                  }
                  loading={isLoadingNaps}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={typeof opt === 'string' ? opt : opt.codigo}
                        label={typeof opt === 'string' ? opt : opt.nombre || opt.codigo}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Paso 3: NAPs afectadas"
                      placeholder="Buscar NAP..."
                      error={!!errors.naps_afectadas}
                      helperText={
                        (errors.naps_afectadas as any)?.message
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingNaps && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}

        {/* Routers */}
        {showRouter && (
          <Grid item xs={12}>
            <Controller
              name="routers_afectados"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={routers}
                  getOptionLabel={(opt) =>
                    typeof opt === 'string'
                      ? opt
                      : `${opt.nombre || ''} - ${opt.ip || ''} (${opt.codigo})`
                  }
                  isOptionEqualToValue={(opt, val) =>
                    (typeof opt === 'string' ? opt : opt.codigo) ===
                    (typeof val === 'string' ? val : val.codigo)
                  }
                  value={routers.filter((r) =>
                    field.value?.includes(r.codigo),
                  )}
                  onChange={(_, newVal) =>
                    field.onChange(
                      newVal.map((v) => (typeof v === 'string' ? v : v.codigo)),
                    )
                  }
                  loading={isLoadingRouters}
                  renderTags={(value, getTagProps) =>
                    value.map((opt, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={typeof opt === 'string' ? opt : opt.codigo}
                        label={typeof opt === 'string' ? opt : opt.nombre || opt.codigo}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Routers afectados"
                      placeholder="Buscar router..."
                      error={!!errors.routers_afectados}
                      helperText={
                        (errors.routers_afectados as any)?.message
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingRouters && (
                              <CircularProgress color="inherit" size={20} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />
          </Grid>
        )}

        {/* Clasificacion (Tipo -> Categoria -> Subcategoria). Opcional. */}
        <Grid item xs={12} md={4}>
          <Controller
            name="tipo_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label="Tipo"
                disabled={isLoadingTipos}
                helperText={tipos.length === 0 && !isLoadingTipos ? 'Sin tipos marcados para masivos' : ' '}
              >
                <MenuItem value="">-- Seleccionar --</MenuItem>
                {tipos.map((t: any) => (
                  <MenuItem key={t.id_ticket_tipo} value={t.id_ticket_tipo}>
                    {t.nombre}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="categoria_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label="Categoria"
                disabled={!tipoSeleccionado || isLoadingCategorias}
              >
                <MenuItem value="">-- Seleccionar --</MenuItem>
                {categorias.map((c: any) => (
                  <MenuItem key={c.id_ticket_categoria} value={c.id_ticket_categoria}>
                    {c.nombre}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Controller
            name="subcategoria_id"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                size="small"
                label="Subcategoria"
                disabled={!categoriaSeleccionada || isLoadingSubcategorias}
              >
                <MenuItem value="">-- Seleccionar --</MenuItem>
                {subcategorias.map((s: any) => (
                  <MenuItem key={s.id_ticket_subcategoria} value={s.id_ticket_subcategoria}>
                    {s.nombre}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        {/* Preview clientes afectados */}
        {previewCount !== null && (
          <Grid item xs={12}>
            <Alert
              severity="info"
              icon={<PeopleIcon />}
              sx={{ fontWeight: 500 }}
            >
              {isLoadingPreview
                ? 'Calculando clientes afectados...'
                : `Se afectaran aproximadamente ${previewCount} cliente(s) con contrato activo.`}
            </Alert>
          </Grid>
        )}

        {/* Check: tiene afectacion */}
        <Grid item xs={12}>
          <Controller
            name="tiene_afectacion"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                }
                label="Tiene afectación de servicio (desmarcar si es falsa alarma)"
              />
            )}
          />
        </Grid>

        {/* Descripcion */}
        <Grid item xs={12}>
          <Controller
            name="descripcion"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Descripcion"
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                error={!!errors.descripcion}
                helperText={errors.descripcion?.message}
              />
            )}
          />
        </Grid>

        {/* Adjuntos (opcional). Se asocian al historial CREACION del
            masivo y quedan visibles en el timeline del detalle. */}
        <Grid item xs={12}>
          <FileDropzoneField
            value={archivos}
            onChange={setArchivos}
            helperText="Adjunte evidencias del masivo (opcional). Imágenes, PDF, ZIP, Excel — máx. 50 MB c/u"
          />
        </Grid>

        <Grid item xs={12} display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
          >
            {isLoading
              ? 'Guardando...'
              : archivos.length > 0
                ? `Crear Masivo (${archivos.length} adjunto${archivos.length > 1 ? 's' : ''})`
                : 'Crear Masivo'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default FormMasivo;
